import React, {useState} from "react";
import { useSelector } from "react-redux";

import getLoop54Client from "loop54-js-connector";

import * as Styled from "./styles.js";

const AddToCartButton = ({
  addToCartLabel,
  context,
  imageBaseUrl,
  lfcMode,
  erpItemNumber,
  channelId,
  loop54EventBaseUrl,
  productId,
  configResult,
  configQuantity,
  articles,
  cvyAddedToCartResponse,
  getApiBaseUrl,
}) => {
  const { language, configString, orderRowDescription, productAttributes } =
    useSelector((state) => state.addToCart);
  const { colorPickerVisible, colorUnique } = useSelector(
    (state) => state.color
  );
  const { colorDimensions, publishLevel } = useSelector((state) => state.dimensions);
    const [disabled, setDisabled] = useState(false);

  const addToCart = () => {
    // If colorUnique is not null, i.e. user has put in a custom color, we need to modify the configurations sent to Scensum backend
    // so that the unique color put in by the user replaces the more general attribute that comes from Lfc backend

    let newConfigString = "";
    let newOrderRowDescription = orderRowDescription;
    let newProductAttributes = productAttributes;

    if (colorDimensions.length && configString.length) {
      newConfigString = colorUnique
        ? configString.replace("OPTIONAL_COLOR", colorUnique)
        : configString;

      const optionalColorDimensionValueProps = colorDimensions[0].values.filter(
        (value) => value.factoryValueCode === "OPTIONAL_COLOR"
      );
      const optionalColorValueName = () => {
        switch (language) {
          case "sv":
            return optionalColorDimensionValueProps[0].valueNameSE;
          case "no":
            return optionalColorDimensionValueProps[0].valueNameNO;
          case "fi":
            return optionalColorDimensionValueProps[0].valueNameFI;
          default:
            return optionalColorDimensionValueProps[0].valueNameEN;
        }
      };

      newOrderRowDescription = colorUnique
        ? orderRowDescription.replace(optionalColorValueName(), colorUnique)
        : orderRowDescription;

      newProductAttributes = colorUnique
        ? productAttributes.map((attribute) => {
            return attribute.attributeValue === optionalColorValueName()
              ? { ...attribute, attributeValue: colorUnique }
              : { ...attribute };
          })
        : productAttributes;
    }

    const parseConfigProductForCart = (
      configProduct,
      quantity,
      chosenValues
    ) => {
      const { prodNo, pricingInfo, weightVolumeDimensions, editorial } =
        configProduct;

      const { images } = editorial;

      return {
        Sku: prodNo,
        Quantity: quantity || 1,
        Type: "configProduct",
        ContextId: context,
        ChosenValues: chosenValues,
        ConfigString: newConfigString ? newConfigString : configString,
        OrderRowDescription: newOrderRowDescription,
        PricingInfo: pricingInfo,
        // Todo, make this easier...
        WeightVolumeDimensions: weightVolumeDimensions
          ? {
              WeightInKg:
                weightVolumeDimensions.weightInKg ||
                weightVolumeDimensions.weightInKg === 0
                  ? weightVolumeDimensions.weightInKg
                  : null,
              WidthInMeters:
                weightVolumeDimensions.widthInMeters ||
                weightVolumeDimensions.widthInMeters === 0
                  ? weightVolumeDimensions.widthInMeters
                  : null,
              DepthInMeters:
                weightVolumeDimensions.depthInMeters ||
                weightVolumeDimensions.depthInMeters === 0
                  ? weightVolumeDimensions.depthInMeters
                  : null,
              HeightInMeters:
                weightVolumeDimensions.heightInMeters ||
                weightVolumeDimensions.heightInMeters === 0
                  ? weightVolumeDimensions.heightInMeters
                  : null,
              FreightVolumeCategory:
                weightVolumeDimensions.freightVolumeCategory || null,
            }
          : null,
        Editorial: {
          Images: images[0]
            ? [
                {
                  imageId: images[0].imageId,
                  // ImagePath is null in response from lfc-backend so currently we generate the path with the help
                  // from an imagepath passed as a prop from skbv
                  imageUrl:
                    imageBaseUrl && images[0].imageId
                      ? `${imageBaseUrl}${images[0].imageId}`
                      : null,
                  ordinal: images[0].ordinal,
                  type: images[0].type,
                },
              ]
            : [],
          Attributes: newProductAttributes,
        },
      };
    };

    const body = JSON.stringify(
      lfcMode === "WDW" || lfcMode === "DOOR"
        ? [
            parseConfigProductForCart(
              configResult.configProducts[0],
              configQuantity,
              configResult.chosenValues
            ),
          ]
        : articles.map((item) => ({
            Sku: item.Sku,
            Quantity: item.Quantity,
            LfcItemNumber: erpItemNumber || null,
          }))
    );

    setDisabled(true);

    fetch(`${getApiBaseUrl(channelId)}/cart/addjson`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json", // Add Content-Type header
      },
      body,
      credentials: "same-origin",
    })
      .then((response) => {
        if (response.status === 200) {
          const customizedMessage =
            lfcMode === "WDW" || lfcMode === "DOOR"
              ? null
              : cvyAddedToCartResponse;

          if (channelId !== "2" && channelId !== "9") {
            // Send addToCart-event to LOOP54
            const loop54Url = loop54EventBaseUrl;
            const client = getLoop54Client(loop54Url);
            client.createEvent(
              "addtocart",
              { type: "Product", id: erpItemNumber },
              (res) => {
                if (res.data.error) {
                  console.error(JSON.stringify(res.data.error));
                }
              }
            );
          }

          // invoking function in Master_bottom_javascript
          window.showAddToCartResponse(productId, null, customizedMessage);
          setDisabled(false);
        } else if (response.status === 404) {
          response.json().then((body) => {
            const { errors } = body;

            if (
              errors &&
              errors.some((error) => error.code === "item_not_added_to_cart")
            ) {
              this.setState(
                () => ({ isBuyable: false }),
                () => {
                  errors.forEach((error) => {
                    console.error(`LFC: ${error.message}`);
                  });
                }
              );
            }
          });
        }
      })
      .catch((error) => {
        console.error(JSON.stringify(error));
      });
  };

  return (
    <Styled.AddToCartButton
      disabled={publishLevel === 80 || (colorPickerVisible && !colorUnique)}
      className={`lfc-add-to-cart-button ${disabled ? "is-disabled" : ""}`}
      onClick={addToCart}
    >
      {addToCartLabel}
      <div className="button-loader"></div>
      <div className="button-loader-ellipsis">&nbsp;</div>
    </Styled.AddToCartButton>
  );
};

export default AddToCartButton;
